import { useRef, useState } from 'react';
import { Info, Copy, ExternalLink, X, Check } from 'lucide-react';
import { copySvgAsImage } from './charts/svgExport.js';

/**
 * Définitions des métriques (popover Info). Description + formule + exclusions.
 */
export const METRIC_DEFINITIONS = {
  utilization: {
    title: 'Taux d’utilisation',
    description:
      'Part de la capacité théorique couverte par la demande (allocations planifiées) ou les heures saisies, selon le mode.',
    formula: 'utilisation = heures (planifiées ou saisies) / capacité théorique',
    exclusions:
      'Capacité = 8h/jour ouvré, moins jours fériés et congés validés. Week-ends et périodes hors emploi exclus.',
  },
  billableUtilization: {
    title: 'Utilisation facturable',
    description:
      'Part de la capacité couverte par des heures marquées facturables.',
    formula: 'utilisation facturable = heures facturables / capacité théorique',
    exclusions: 'Les heures non facturables ne sont pas comptées au numérateur.',
  },
  deliveryCapacity: {
    title: 'Capacité de livraison',
    description:
      'Capacité théorique de production de l’équipe sur la période, comparée à la demande.',
    formula: 'capacité = Σ (jours ouvrés × 8h) − fériés − congés validés',
    exclusions: 'Week-ends, jours fériés, congés validés et périodes hors emploi exclus.',
  },
};

const headerBtn = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 28,
  height: 28,
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--glass-border)',
  background: 'var(--glass)',
  color: 'var(--text-muted)',
  cursor: 'pointer',
};

export default function DashboardWidget({
  title,
  metricKey,
  loading = false,
  onDrillDown,
  children,
  footer,
}) {
  const bodyRef = useRef(null);
  const [showInfo, setShowInfo] = useState(false);
  const [copyState, setCopyState] = useState(null); // 'clipboard' | 'download' | 'error' | null
  const def = metricKey ? METRIC_DEFINITIONS[metricKey] : null;

  const handleCopy = async () => {
    const svg = bodyRef.current?.querySelector('svg');
    if (!svg) return;
    try {
      const bgVar = getComputedStyle(document.documentElement)
        .getPropertyValue('--card-bg')
        .trim();
      const mode = await copySvgAsImage(svg, { background: bgVar || '#1e293b', filename: `${title || 'rapport'}.png` });
      setCopyState(mode);
    } catch {
      setCopyState('error');
    }
    setTimeout(() => setCopyState(null), 2500);
  };

  return (
    <div
      style={{
        background: 'var(--card-bg)',
        border: '1px solid var(--glass-border)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--shadow-card)',
        padding: 16,
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{title}</h3>
        <div style={{ display: 'flex', gap: 6 }}>
          {def && (
            <button
              type="button"
              title="Définition de la métrique"
              style={headerBtn}
              onClick={() => setShowInfo((v) => !v)}
            >
              <Info size={15} />
            </button>
          )}
          <button
            type="button"
            title={
              copyState === 'clipboard'
                ? 'Copié dans le presse-papier'
                : copyState === 'download'
                  ? 'Image téléchargée'
                  : 'Copier en image'
            }
            style={{
              ...headerBtn,
              color: copyState ? 'var(--success)' : 'var(--text-muted)',
            }}
            onClick={handleCopy}
          >
            {copyState && copyState !== 'error' ? <Check size={15} /> : <Copy size={15} />}
          </button>
          {onDrillDown && (
            <button
              type="button"
              title="Voir le rapport détaillé"
              style={headerBtn}
              onClick={onDrillDown}
            >
              <ExternalLink size={15} />
            </button>
          )}
        </div>
      </div>

      {showInfo && def && (
        <div
          style={{
            position: 'absolute',
            top: 48,
            right: 16,
            zIndex: 5,
            width: 280,
            background: 'var(--card-bg)',
            border: '1px solid var(--glass-border)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-card)',
            padding: 14,
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <strong style={{ fontSize: 13, color: 'var(--text)' }}>{def.title}</strong>
            <button type="button" style={{ ...headerBtn, width: 22, height: 22 }} onClick={() => setShowInfo(false)}>
              <X size={13} />
            </button>
          </div>
          <p style={{ margin: '0 0 8px', fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}>
            {def.description}
          </p>
          <code
            style={{
              display: 'block',
              fontSize: 11,
              color: 'var(--accent)',
              background: 'var(--glass)',
              padding: '6px 8px',
              borderRadius: 'var(--radius-sm)',
              marginBottom: 8,
            }}
          >
            {def.formula}
          </code>
          <p style={{ margin: 0, fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.5 }}>
            {def.exclusions}
          </p>
        </div>
      )}

      <div ref={bodyRef}>
        {loading ? (
          <div
            data-testid="widget-skeleton"
            style={{
              height: 220,
              borderRadius: 'var(--radius-sm)',
              background:
                'linear-gradient(90deg, var(--glass) 25%, var(--glass-border) 50%, var(--glass) 75%)',
              backgroundSize: '200% 100%',
              animation: 'synolog-skeleton 1.4s ease-in-out infinite',
            }}
          />
        ) : (
          children
        )}
      </div>

      {footer && !loading && (
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{footer}</div>
      )}

      <style>{`@keyframes synolog-skeleton {0%{background-position:200% 0}100%{background-position:-200% 0}}`}</style>
    </div>
  );
}
